import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import type {
  ExternalCalendarAvailabilityWindow,
  ExternalCalendarEvent,
} from '@/types/calendar';

export interface BlockPosition {
  topPct: number;
  heightPct: number;
}

export interface PositionedBlock extends BlockPosition {
  id: string;
}

export interface HorizontalBlockPosition {
  leftPct: number;
  widthPct: number;
}

export const layoutOverlappingBlocks = (
  blocks: PositionedBlock[],
): Map<string, HorizontalBlockPosition> => {
  const sorted = [...blocks].sort(
    (left, right) =>
      left.topPct - right.topPct || right.heightPct - left.heightPct,
  );
  const layout = new Map<string, HorizontalBlockPosition>();

  while (sorted.length > 0) {
    const first = sorted.shift();
    if (!first) break;
    const group = [first];
    let groupEnd = first.topPct + first.heightPct;

    while (sorted[0] && sorted[0].topPct < groupEnd) {
      const block = sorted.shift();
      if (!block) break;
      group.push(block);
      groupEnd = Math.max(groupEnd, block.topPct + block.heightPct);
    }

    const columnEnds: number[] = [];
    const columns = new Map<string, number>();
    group.forEach((block) => {
      const availableColumn = columnEnds.findIndex(
        (end) => end <= block.topPct,
      );
      const column =
        availableColumn === -1 ? columnEnds.length : availableColumn;
      columnEnds[column] = block.topPct + block.heightPct;
      columns.set(block.id, column);
    });

    const widthPct = 100 / columnEnds.length;
    group.forEach((block) => {
      layout.set(block.id, {
        leftPct: (columns.get(block.id) ?? 0) * widthPct,
        widthPct,
      });
    });
  }

  return layout;
};

const minutesToPosition = (
  startMinute: number,
  endMinute: number,
  dayStartHour: number,
  dayEndHour: number,
): BlockPosition => {
  const windowMinutes = (dayEndHour - dayStartHour) * 60;
  const windowStart = dayStartHour * 60;
  return {
    heightPct: ((endMinute - startMinute) / windowMinutes) * 100,
    topPct: ((startMinute - windowStart) / windowMinutes) * 100,
  };
};

// Rails Blueprinter renders datetimes with #httpdate (config/initializers/blueprinter.rb).
export const RFC_7231_TIMESTAMP =
  /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d{2} (?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4} \d{2}:\d{2}:\d{2} GMT$/u;

const parseCalendarTimestamp = (timestamp: string): Date => {
  if (!RFC_7231_TIMESTAMP.test(timestamp)) return parseISO(timestamp);

  const date = new Date(timestamp);
  return date.toUTCString() === timestamp ? date : parseISO(timestamp);
};

const getTimeParts = (
  iso: string,
  timeZone?: string | null,
): { hours: number; minutes: number } => {
  const date = parseCalendarTimestamp(iso);
  if (!timeZone) {
    return { hours: date.getHours(), minutes: date.getMinutes() };
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    timeZone,
  }).formatToParts(date);
  const value = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { hours: value('hour'), minutes: value('minute') };
};

const getDateParts = (
  date: Date,
  timeZone?: string | null,
): { day: number; month: number; year: number } => {
  if (!timeZone) {
    return {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(date);
  const value = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { day: value('day'), month: value('month'), year: value('year') };
};

const toDateKey = ({
  day,
  month,
  year,
}: {
  day: number;
  month: number;
  year: number;
}) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const calendarDate = (dateKey: string): Date => new Date(`${dateKey}T12:00:00`);

const anchorForTimeZone = (anchor: Date, timeZone?: string | null): Date =>
  calendarDate(toDateKey(getDateParts(anchor, timeZone)));

export const dayKeyFromIso = (
  iso: string,
  timeZone?: string | null,
): string => {
  if (/^\d{4}-\d{2}-\d{2}$/u.test(iso)) return iso;
  const date = parseCalendarTimestamp(iso);
  if (!timeZone) return format(date, 'yyyy-MM-dd');

  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(date);
  const value = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
};

export const formatDateKey = (
  dateKey: string,
  options: { weekday?: boolean } = {},
): string => {
  const [year = 0, month = 1, day = 1] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  return new Intl.DateTimeFormat('en-US', {
    ...(options.weekday ? { weekday: 'short' } : {}),
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(date);
};

const dateKeyFromDate = (date: Date): string => toDateKey(getDateParts(date));

const dateKeyFromInstant = (date: Date, timeZone?: string | null): string =>
  toDateKey(getDateParts(date, timeZone));

export const formatIsoTime = (
  iso: string,
  timeZone?: string | null,
): string => {
  try {
    const date = parseCalendarTimestamp(iso);
    if (!timeZone) return format(date, 'h:mmaaa');
    return date
      .toLocaleTimeString('en', {
        hour: 'numeric',
        hour12: true,
        minute: '2-digit',
        timeZone,
      })
      .replace(/\s/u, '')
      .toLowerCase();
  } catch {
    return '';
  }
};

export const formatIsoTimeRange = (
  startedAt: string,
  endedAt: string,
  timeZone?: string | null,
): string => {
  const start = formatIsoTime(startedAt, timeZone);
  const end = formatIsoTime(endedAt, timeZone);
  if (!start || !end) return '';
  return `${start}-${end}`;
};

export const formatIsoDateTimeRange = (
  startedAt: string,
  endedAt: string,
  timeZone?: string | null,
): string => {
  try {
    const start = parseCalendarTimestamp(startedAt);
    const date = timeZone
      ? start.toLocaleDateString('en', {
          day: 'numeric',
          month: 'long',
          timeZone,
          weekday: 'long',
        })
      : format(start, 'EEEE, MMMM d');
    const startTime = formatIsoTime(startedAt, timeZone);
    const endTime = formatIsoTime(endedAt, timeZone);
    return `${date} • ${startTime} – ${endTime}`;
  } catch {
    return '';
  }
};

// Sunday-anchored week containing the given date.
export const getWeekDays = (anchor: Date, timeZone?: string | null): Date[] => {
  const start = startOfWeek(anchorForTimeZone(anchor, timeZone), {
    weekStartsOn: 0,
  });
  return Array.from({ length: 7 }, (_unused, index) => addDays(start, index));
};

// Inclusive [start, end] of the Sunday-anchored week containing the date.
export const getWeekRange = (
  anchor: Date,
  timeZone?: string | null,
): [Date, Date] => {
  const zonedAnchor = anchorForTimeZone(anchor, timeZone);
  return [
    startOfWeek(zonedAnchor, { weekStartsOn: 0 }),
    endOfWeek(zonedAnchor, { weekStartsOn: 0 }),
  ];
};

// Inclusive [start, end] of the full month grid (padded to Sun..Sat weeks).
export const getMonthRange = (
  anchor: Date,
  timeZone?: string | null,
): [Date, Date] => {
  const zonedAnchor = anchorForTimeZone(anchor, timeZone);
  return [
    startOfWeek(startOfMonth(zonedAnchor), { weekStartsOn: 0 }),
    endOfWeek(endOfMonth(zonedAnchor), { weekStartsOn: 0 }),
  ];
};

// Weeks (rows of 7) covering the anchor's month, padded to full Sun..Sat weeks.
export const getMonthMatrix = (
  anchor: Date,
  timeZone?: string | null,
): Date[][] => {
  const zonedAnchor = anchorForTimeZone(anchor, timeZone);
  const gridStart = startOfWeek(startOfMonth(zonedAnchor), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(zonedAnchor), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ end: gridEnd, start: gridStart });

  const weeks: Date[][] = [];
  for (let index = 0; index < days.length; index += 7) {
    weeks.push(days.slice(index, index + 7));
  }
  return weeks;
};

// Vertical position (%) of a timed block within a [dayStartHour, dayEndHour]
// window, clamped to the visible window.
// MEN-4122: the week grid opened on a fixed hour, so a coach whose day starts
// later than that saw an empty grid and had to scroll to find their sessions.
// Open one hour before the earliest session in view instead, falling back to
// the fixed hour when the week has none.
export const initialScrollHour = (
  startIsos: readonly string[],
  fallbackHour: number,
  timeZone?: string | null,
): number => {
  if (startIsos.length === 0) return fallbackHour;
  const hours = startIsos.map((iso) => getTimeParts(iso, timeZone).hours);
  return Math.max(0, Math.min(...hours) - 1);
};

export const DEFAULT_VISIBLE_START_HOUR = 8;
export const DEFAULT_VISIBLE_END_HOUR = 18;

export interface HourWindow {
  startHour: number;
  endHour: number;
}

/**
 * MEN-4239: the Week grid renders only the hours that matter instead of a
 * full 24-hour scroll. The window opens at 8am or the earliest thing in view,
 * whichever is earlier, and closes at 6pm or the latest thing in view,
 * whichever is later — where "in view" means both the coach's availability
 * windows and anything actually scheduled (sessions and external events).
 *
 * Hours are floored at the start and ceiled at the end so a 7:15am session
 * opens the grid at 7am rather than clipping its first quarter hour. An
 * interval whose end is at or before its start has crossed midnight, and the
 * grid renders it as two segments — late on one day, early on the next — so it
 * opens the whole day rather than hiding half of itself.
 */
export const visibleHourWindow = (
  intervals: readonly { start: string; end: string }[],
  availabilityWindows?: readonly ExternalCalendarAvailabilityWindow[] | null,
  timeZone?: string | null,
): HourWindow => {
  let startHour = DEFAULT_VISIBLE_START_HOUR;
  let endHour = DEFAULT_VISIBLE_END_HOUR;

  intervals.forEach(({ end, start }) => {
    const from = getTimeParts(start, timeZone);
    const to = getTimeParts(end, timeZone);
    const fromMinutes = from.hours * 60 + from.minutes;
    const toMinutes = to.hours * 60 + to.minutes;
    const elapsedMs =
      parseCalendarTimestamp(end).getTime() -
      parseCalendarTimestamp(start).getTime();
    if (
      elapsedMs >= 24 * 60 * 60 * 1000 ||
      dayKeyFromIso(start, timeZone) !== dayKeyFromIso(end, timeZone)
    ) {
      startHour = 0;
      endHour = 24;
      return;
    }
    if (toMinutes <= fromMinutes) {
      startHour = 0;
      endHour = 24;
      return;
    }
    startHour = Math.min(startHour, Math.floor(fromMinutes / 60));
    endHour = Math.max(endHour, Math.ceil(toMinutes / 60));
  });

  (availabilityWindows ?? []).forEach((window) => {
    startHour = Math.min(startHour, Math.floor(window.startMinute / 60));
    endHour = Math.max(endHour, Math.ceil(window.endMinute / 60));
  });

  return {
    endHour: Math.min(24, Math.max(endHour, startHour + 1)),
    startHour: Math.max(0, startHour),
  };
};

export const blockPosition = (
  startIso: string,
  endIso: string,
  dayStartHour: number,
  dayEndHour: number,
  timeZone?: string | null,
): BlockPosition => {
  const windowMinutes = (dayEndHour - dayStartHour) * 60;
  const start = getTimeParts(startIso, timeZone);
  const end = getTimeParts(endIso, timeZone);

  const startMinutes = start.hours * 60 + start.minutes;
  const endMinutes = end.hours * 60 + end.minutes;
  const windowStart = dayStartHour * 60;
  const normalizedEndMinutes =
    endMinutes <= startMinutes ? endMinutes + 24 * 60 : endMinutes;

  const clampedStart = Math.max(0, startMinutes - windowStart);
  const clampedEnd = Math.min(
    windowMinutes,
    normalizedEndMinutes - windowStart,
  );

  const topPct = (clampedStart / windowMinutes) * 100;
  const heightPct = Math.max(
    0,
    ((clampedEnd - clampedStart) / windowMinutes) * 100,
  );

  return { heightPct, topPct };
};

export const blockPositionForDay = (
  startIso: string,
  endIso: string,
  day: Date,
  dayStartHour: number,
  dayEndHour: number,
  timeZone?: string | null,
): BlockPosition => {
  const dayKey = dateKeyFromDate(day);
  const startKey = dayKeyFromIso(startIso, timeZone);
  const endKey = dayKeyFromIso(endIso, timeZone);
  const start =
    startKey === dayKey
      ? getTimeParts(startIso, timeZone)
      : {
          hours: dayStartHour,
          minutes: 0,
        };
  const end =
    endKey === dayKey
      ? getTimeParts(endIso, timeZone)
      : {
          hours: dayEndHour,
          minutes: 0,
        };
  const startMinutes = start.hours * 60 + start.minutes;
  const endMinutes = end.hours * 60 + end.minutes;
  const elapsedMinutes =
    (parseCalendarTimestamp(endIso).getTime() -
      parseCalendarTimestamp(startIso).getTime()) /
    (60 * 1000);
  const wallClockMinutes = endMinutes - startMinutes;
  let normalizedEndMinutes = endMinutes;
  if (endKey === startKey && elapsedMinutes > wallClockMinutes) {
    normalizedEndMinutes = startMinutes + elapsedMinutes;
  } else if (endKey === startKey && endMinutes <= startMinutes) {
    normalizedEndMinutes = endMinutes + 24 * 60;
  }
  const windowMinutes = (dayEndHour - dayStartHour) * 60;
  const windowStart = dayStartHour * 60;
  const clampedStart = Math.max(0, startMinutes - windowStart);
  const clampedEnd = Math.min(
    windowMinutes,
    normalizedEndMinutes - windowStart,
  );

  return {
    heightPct: Math.max(0, ((clampedEnd - clampedStart) / windowMinutes) * 100),
    topPct: (clampedStart / windowMinutes) * 100,
  };
};

// True when the ISO datetime falls on the given local calendar day.
export const sameLocalDay = (iso: string, day: Date): boolean => {
  try {
    return isSameDay(parseCalendarTimestamp(iso), day);
  } catch {
    return false;
  }
};

// Local day key (yyyy-MM-dd) for a Date.
export const dayKeyFor = (day: Date): string => dateKeyFromDate(day);

// Buckets items into a Map keyed by local day, computed once so calendar cells
// can do O(1) lookups instead of filtering the whole list per cell.
export const bucketByDay = <T>(
  items: T[],
  getIso: (item: T) => string,
  timeZone?: string | null,
): Map<string, T[]> => {
  const buckets = new Map<string, T[]>();
  items.forEach((item) => {
    let key: string;
    try {
      key = dayKeyFromIso(getIso(item), timeZone);
    } catch {
      return;
    }
    const bucket = buckets.get(key) ?? [];
    bucket.push(item);
    buckets.set(key, bucket);
  });
  return buckets;
};

export const bucketIntervalsByDay = <T>(
  items: T[],
  getStartIso: (item: T) => string,
  getEndIso: (item: T) => string,
  timeZone?: string | null,
): Map<string, T[]> => {
  const buckets = new Map<string, T[]>();

  items.forEach((item) => {
    try {
      const startIso = getStartIso(item);
      const start = parseCalendarTimestamp(startIso);
      const end = parseCalendarTimestamp(getEndIso(item));
      if (!Number.isFinite(start.getTime()) || end <= start) return;

      const startKey = dayKeyFromIso(startIso, timeZone);
      const endKey = dateKeyFromInstant(new Date(end.getTime() - 1), timeZone);
      eachDayOfInterval({
        end: calendarDate(endKey),
        start: calendarDate(startKey),
      }).forEach((day) => {
        const key = dayKeyFor(day);
        const bucket = buckets.get(key) ?? [];
        bucket.push(item);
        buckets.set(key, bucket);
      });
    } catch {
      // Ignore malformed calendar intervals without hiding valid items.
    }
  });

  return buckets;
};

export const bucketEventsByDay = (
  events: ExternalCalendarEvent[],
  timeZone?: string | null,
): Map<string, ExternalCalendarEvent[]> => {
  const buckets = new Map<string, ExternalCalendarEvent[]>();

  events.forEach((event) => {
    try {
      const start = parseCalendarTimestamp(event.start);
      const end = parseCalendarTimestamp(event.end);
      if (
        !Number.isFinite(start.getTime()) ||
        !Number.isFinite(end.getTime()) ||
        (event.allDay ? end < start : end <= start)
      )
        return;

      if (!event.allDay) {
        const startKey = dayKeyFromIso(event.start, timeZone);
        const endKey = dateKeyFromInstant(
          new Date(end.getTime() - 1),
          timeZone,
        );
        eachDayOfInterval({
          end: calendarDate(endKey),
          start: calendarDate(startKey),
        }).forEach((day) => {
          const key = dayKeyFor(day);
          const bucket = buckets.get(key) ?? [];
          bucket.push(event);
          buckets.set(key, bucket);
        });
        return;
      }

      const startKey = dayKeyFromIso(event.start, timeZone);
      const endKey = dayKeyFromIso(event.end, timeZone);
      if (endKey < startKey) return;
      const isExclusiveDateEnd =
        /^\d{4}-\d{2}-\d{2}$/u.test(event.end) && endKey > startKey;
      const inclusiveEnd = isExclusiveDateEnd
        ? addDays(calendarDate(endKey), -1)
        : calendarDate(endKey);

      eachDayOfInterval({
        end: inclusiveEnd,
        start: calendarDate(startKey),
      }).forEach((day) => {
        const key = dayKeyFor(day);
        const bucket = buckets.get(key) ?? [];
        bucket.push(event);
        buckets.set(key, bucket);
      });
    } catch {
      // Ignore malformed provider events without hiding valid neighbors.
    }
  });

  return buckets;
};

export const availabilityUnavailableBlocksForDay = (
  availabilityWindows: ExternalCalendarAvailabilityWindow[],
  day: Date,
  dayStartHour: number,
  dayEndHour: number,
): BlockPosition[] => {
  const weekday = day.getDay() === 0 ? 7 : day.getDay();
  const windowStart = dayStartHour * 60;
  const windowEnd = dayEndHour * 60;
  const available = availabilityWindows
    .filter((window) => window.weekday === weekday)
    .map((window) => ({
      end: Math.min(windowEnd, window.endMinute),
      start: Math.max(windowStart, window.startMinute),
    }))
    .filter((window) => window.end > window.start)
    .sort((left, right) => left.start - right.start || left.end - right.end);

  const blocks: BlockPosition[] = [];
  let cursor = windowStart;
  available.forEach((window) => {
    if (window.start > cursor) {
      blocks.push(
        minutesToPosition(cursor, window.start, dayStartHour, dayEndHour),
      );
    }
    cursor = Math.max(cursor, window.end);
  });
  if (cursor < windowEnd) {
    blocks.push(minutesToPosition(cursor, windowEnd, dayStartHour, dayEndHour));
  }
  return blocks;
};

const MINUTES_PER_DAY = 24 * 60;
const DAY_MS = MINUTES_PER_DAY * 60 * 1000;

const utcDayFromKey = (dateKey: string): number => {
  const [year = 0, month = 1, day = 1] = dateKey.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};

const isoWeekdayOfUtcDay = (utcDay: number): number => {
  const weekday = new Date(utcDay).getUTCDay();
  return weekday === 0 ? 7 : weekday;
};

const zoneOffsetMs = (instant: number, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(new Date(instant));
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  const wallAsUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second'),
  );
  return wallAsUtc - Math.floor(instant / 1000) * 1000;
};

// The instant at which `minute` minutes after midnight of the UTC-encoded
// calendar day `utcDay` occurs on the wall clock in `timeZone`.
const zonedWallTimeToInstant = (
  utcDay: number,
  minute: number,
  timeZone: string,
): number => {
  const target = utcDay + minute * 60 * 1000;
  let candidate = target;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const next = target - zoneOffsetMs(candidate, timeZone);
    if (next === candidate) break;
    candidate = next;
  }
  return candidate;
};

const MINUTE_MS = 60 * 1000;

// Wall-clock minutes are only monotonic while the zone's offset holds, so a
// range crossing a DST change is split at the change. Each piece then maps to
// wall-clock minutes from its start plus its elapsed duration: across a
// fall-back overlap both pieces cover the repeated hour instead of collapsing
// to equal endpoints, and across a spring-forward gap the second piece starts
// after the skipped hour. A display day holds at most one change.
const splitAtOffsetChange = (
  start: number,
  end: number,
  timeZone: string,
): [number, number][] => {
  const startOffset = zoneOffsetMs(start, timeZone);
  if (zoneOffsetMs(end - MINUTE_MS, timeZone) === startOffset) {
    return [[start, end]];
  }
  let before = start;
  let after = end - MINUTE_MS;
  while (after - before > MINUTE_MS) {
    const middle =
      before + Math.floor((after - before) / 2 / MINUTE_MS) * MINUTE_MS;
    if (zoneOffsetMs(middle, timeZone) === startOffset) before = middle;
    else after = middle;
  }
  return [
    [start, after],
    [after, end],
  ];
};

/**
 * MEN-5421: Moments expresses availability windows as wall-clock minutes in
 * the coach's availability timezone, but the admin grid renders in the
 * viewer's timezone. Re-express the windows for the displayed days in the
 * display zone. The week grid shows seven distinct weekdays, so weekday-keyed
 * output is exact for those days — including DST weeks and windows that cross
 * midnight in the display zone, which split across two days.
 */
export const availabilityWindowsInTimeZone = (
  availabilityWindows: readonly ExternalCalendarAvailabilityWindow[],
  days: readonly Date[],
  availabilityTimeZone?: string | null,
  displayTimeZone?: string | null,
): ExternalCalendarAvailabilityWindow[] => {
  if (
    !availabilityTimeZone ||
    !displayTimeZone ||
    availabilityTimeZone === displayTimeZone
  ) {
    return [...availabilityWindows];
  }

  const converted: ExternalCalendarAvailabilityWindow[] = [];
  days.forEach((day) => {
    const displayDay = utcDayFromKey(dayKeyFor(day));
    const weekday = isoWeekdayOfUtcDay(
      displayDay,
    ) as ExternalCalendarAvailabilityWindow['weekday'];
    const dayStart = zonedWallTimeToInstant(displayDay, 0, displayTimeZone);
    const dayEnd = zonedWallTimeToInstant(
      displayDay + DAY_MS,
      0,
      displayTimeZone,
    );

    // Real zone offsets differ by up to 26 hours, so a coach-local date two
    // days either side can still reach into this display day.
    for (let shift = -2; shift <= 2; shift += 1) {
      const sourceDay = displayDay + shift * DAY_MS;
      const sourceWeekday = isoWeekdayOfUtcDay(sourceDay);
      availabilityWindows
        .filter((window) => window.weekday === sourceWeekday)
        .forEach((window) => {
          const start = Math.max(
            dayStart,
            zonedWallTimeToInstant(
              sourceDay,
              window.startMinute,
              availabilityTimeZone,
            ),
          );
          const end = Math.min(
            dayEnd,
            zonedWallTimeToInstant(
              sourceDay,
              window.endMinute,
              availabilityTimeZone,
            ),
          );
          if (end <= start) return;
          splitAtOffsetChange(start, end, displayTimeZone).forEach(
            ([segmentStart, segmentEnd]) => {
              const parts = getTimeParts(
                new Date(segmentStart).toISOString(),
                displayTimeZone,
              );
              const startMinute = parts.hours * 60 + parts.minutes;
              converted.push({
                endMinute: Math.min(
                  MINUTES_PER_DAY,
                  startMinute + (segmentEnd - segmentStart) / MINUTE_MS,
                ),
                startMinute,
                weekday,
              });
            },
          );
        });
    }
  });
  return converted;
};
